import { useReducer, useState, useEffect } from 'react'
import classnames from 'classnames'
import Router from 'next/router'
import { APIErrors } from '../../../api-client/enums'
import { formatPhoneNumber } from '../../../utils/format-phone-number'
import { toCapitalize } from '../../../utils/to-capitalize'
import { FormField, Select } from '../../../components/inputs'
import { Button } from '../../../components/button'
import useValidation from '../../../hooks/use-validation'
import { ROUTES } from '../../../constants'
import useAvailableMemberships from '../../../hooks/use-available-memberhips'
import useAvailableGroups from '../../../hooks/use-available-groups'
import compare from '../../../utils/compare'

const re = {
  email: /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
  password: /^(?=^.{8,}$)((?!.*\s)(?=.*[a-z]))((?=(.*\d){1,})|(?=(.*\W){1,}))^.*$/i,
  phoneNumber: /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im,
  zipCode: /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/,
}

const FormFields = {
  FirstName: 'firstName',
  LastName: 'lastName',
  Email: 'email',
  PhoneNumber: 'phoneNumber',
  ZipCode: 'zipCode',
  Password: 'password',
  ConfirmPassword: 'confirmPassword',
  Membership: 'membership',
  MembershipGroup: 'membershipGroup',
  Type: 'type',
  MemberID: 'memberID',
}

const validationErrors = {
  email: 'Email is not valid',
  phoneNumber: 'Mobile Phone number must be 10 digits long',
  password: "Password doesn't meet strength requirements",
  confirmPassword: "Passwords doesn't match",
  zipCode: 'Please enter a valid postal code',
  membership: 'Please select a Membership',
  group: 'Please select a Group',
  type: 'Please select a Type',
  memberID: 'Please enter a Member ID',
}

const generateUserObject = ({
  firstName,
  lastName,
  email,
  phoneNumber,
  zipCode,
  password,
  confirmPassword,
}) => {
  return {
    firstName: toCapitalize(firstName.value),
    lastName: toCapitalize(lastName.value),
    email: email.value,
    phoneNumber: phoneNumber.value.replace(/[^\d]/g, ''),
    zipCode: zipCode.value,
    password: password.value,
    confirmPassword: confirmPassword.value,
  }
}

const generateMembershipObject = (membership, group, type, memberID, userId) => {
  return {
    benefitsProgramId: membership,
    benefitsProgramGroupId: group,
    membershipType: type,
    membershipTypeDetails: memberID,
    userId: userId,
  }
}

const isFieldValid = (field, state) => re[field].test(state[field].value)

const isFieldEmpty = (entry) => (entry.value === '' ? true : false)

const reducer = (state, action) => ({
  ...state,
  [action.payload.field]: {
    ...state[action.payload.field],
    value: action.payload.value,
  },
})

const initialState = {
  firstName: { value: '' },
  lastName: { value: '' },
  email: { value: '' },
  phoneNumber: { value: '' },
  zipCode: { value: '' },
  password: { value: '' },
  confirmPassword: { value: '' },
}

const membershipTypes = [
  { value: 1, label: 'Member' },
  { value: 2, label: 'Friends and Family' },
]

export default function RegisterWithMembership({
  onSubmit,
  onMembershipSubmit,
  disabled,
  serverErrors,
  onLogin,
  submittedForm,
  registeredSuccessful,
  userId,
  color,
}) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [showErrors, { isFormValid, errors }] = useValidation(state, validate)
  const [mappedServerErrors, setMappedServerErrors] = useState(() =>
    mapServerErrors(serverErrors?.errors),
  )
  const [submitMembership, setSubmitMembership] = useState(false)
  const [registeredUserId, setRegisteredUserId] = useState(null)

  const { availableMemberships } = useAvailableMemberships([])
  const [membership, setMembership] = useState(null)
  const { availableGroups } = useAvailableGroups(membership?.value)
  const [groupValue, setGroupValue] = useState(null)
  const [type, setType] = useState(null)
  const [memberID, setMemberID] = useState('')
  const [validateMembership, setValidateMembership] = useState(false)

  const formattedMemberships = availableMemberships?.programs?.map((data) => {
    return { value: data.benefitsProgramId, label: data.benefitsProgramName }
  })
  const formattedGroups = availableGroups?.groups?.map((data) => {
    return { value: data.groupId, label: data.groupAbbreviation + ' ' + data.groupName }
  })

  useEffect(() => setMappedServerErrors(mapServerErrors(serverErrors?.errors)), [serverErrors])

  useEffect(() => {
    if (registeredSuccessful) {
      setSubmitMembership(registeredSuccessful)
    }
  }, [registeredSuccessful])

  useEffect(() => {
    setRegisteredUserId(userId)
  }, [userId])

  useEffect(() => {
    if (submitMembership && registeredUserId) {
      onMembershipSubmit(
        generateMembershipObject(
          membership?.value,
          groupValue?.value,
          type?.value,
          memberID,
          registeredUserId,
        ),
      )
      setSubmitMembership(false)
    }
  }, [
    submitMembership,
    groupValue?.value,
    memberID,
    membership?.value,
    onMembershipSubmit,
    type?.value,
    registeredUserId,
  ])

  const handleChange = ({ target: { name, value } }) => {
    dispatch({ payload: { field: name, value } })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    setValidateMembership(!(membership && groupValue && type && memberID))

    if (!isFormValid) showErrors()
    else {
      if (!validateMembership) {
        onSubmit(generateUserObject(state))
      }
    }
  }

  const checkFieldForErrors = (field) => {
    return errors[field] ||
      (mappedServerErrors[field] && submittedForm[field] === state[field].value)
      ? true
      : false
  }

  return (
    <form className='wrapper' onSubmit={handleSubmit}>
      <div className='flex alignCenter flexDirectionColumn'>
        <h2 className='title'>Create your account</h2>
        <p className='loginInstead'>
          Already have an account?{' '}
          <button type='button' className='flatBtn' onClick={onLogin}>
            Sign in
          </button>
        </p>
      </div>
      <div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: errors.firstName },
              { visible: errors.firstName },
            )}
            htmlFor='firstName'
          >
            {errors.firstName}
          </label>
          <FormField
            placeholder='First Name'
            type='text'
            id='firstName'
            name={FormFields.FirstName}
            value={state.firstName.value}
            error={errors.firstName}
            onChange={handleChange}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: errors.lastName },
              { visible: errors.lastName },
            )}
            htmlFor='lastName'
          >
            {errors.lastName}
          </label>
          <FormField
            placeholder='Last Name'
            type='text'
            id='lastName'
            name={FormFields.LastName}
            value={state.lastName.value}
            error={errors.lastName}
            onChange={handleChange}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: checkFieldForErrors(FormFields.Email) },
              { visible: checkFieldForErrors(FormFields.Email) },
            )}
            htmlFor='email'
          >
            {errors.email || mappedServerErrors.email}
          </label>
          <FormField
            placeholder='Email'
            type='email'
            id='email'
            name={FormFields.Email}
            value={state.email.value}
            error={checkFieldForErrors(FormFields.Email)}
            onChange={handleChange}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: checkFieldForErrors(FormFields.PhoneNumber) },
              { visible: checkFieldForErrors(FormFields.PhoneNumber) },
            )}
            htmlFor='phoneNumber'
          >
            {errors.phoneNumber || mappedServerErrors.phoneNumber}
          </label>
          <FormField
            placeholder='Mobile Phone'
            type='tel'
            id='phoneNumber'
            inputMode='numeric'
            maxLength='12'
            name={FormFields.PhoneNumber}
            value={formatPhoneNumber(state.phoneNumber.value)}
            error={checkFieldForErrors(FormFields.PhoneNumber)}
            onChange={handleChange}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: checkFieldForErrors(FormFields.ZipCode) },
              { visible: checkFieldForErrors(FormFields.ZipCode) },
            )}
            htmlFor='zipCode'
          >
            {mappedServerErrors.zipCode || errors.zipCode}
          </label>
          <FormField
            placeholder='Postal code'
            id='zipCode'
            inputMode='numeric'
            name={FormFields.ZipCode}
            value={state.zipCode.value}
            error={mappedServerErrors.zipCode || errors.zipCode}
            onChange={handleChange}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: validateMembership && !membership },
              { visible: validateMembership && !membership },
            )}
            htmlFor='membership'
          >
            {validateMembership && !membership && validationErrors.membership}
          </label>
          <Select
            placeholder='Select Membership'
            id='membership'
            name='membership'
            options={formattedMemberships?.sort(compare)}
            onChange={(e) => {
              setGroupValue(null)
              setMembership(e)
            }}
            value={membership}
            error={validateMembership && !membership && validationErrors.membership}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: validateMembership && !groupValue },
              { visible: validateMembership && !groupValue },
            )}
            htmlFor='membershipGroup'
          >
            {validateMembership && !groupValue && validationErrors.group}
          </label>
          <Select
            placeholder='Select Group'
            id='membershipGroup'
            name='membershipGroup'
            options={formattedGroups?.sort(compare)}
            isDisabled={!membership}
            onChange={(e) => {
              setGroupValue(e)
            }}
            value={groupValue}
            error={validateMembership && !groupValue && validationErrors.group}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: validateMembership && !type },
              { visible: validateMembership && !type },
            )}
            htmlFor='type'
          >
            {validateMembership && !type && validationErrors.type}
          </label>
          <Select
            placeholder='Select Type'
            id='type'
            name='type'
            options={membershipTypes}
            isDisabled={!membership}
            onChange={(value) => {
              setType(value)
            }}
            value={type}
            error={validateMembership && !type && validationErrors.type}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: validateMembership && !memberID },
              { visible: validateMembership && !memberID },
            )}
            htmlFor='memberID'
          >
            {validateMembership && !memberID && validationErrors.memberID}
          </label>
          <FormField
            placeholder='Member ID'
            type='text'
            id='memberID'
            name='memberID'
            value={memberID}
            error={validateMembership && !memberID}
            onChange={(e) => setMemberID(e.target.value)}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: errors.password },
              { visible: errors.password },
            )}
            htmlFor='password'
          >
            {errors.password}
          </label>
          <FormField
            placeholder='Password'
            type='password'
            id='password'
            name={FormFields.Password}
            value={state.password.value}
            error={errors.password}
            onChange={handleChange}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: errors.confirmPassword },
              { visible: errors.confirmPassword },
            )}
            htmlFor='confirmPassword'
          >
            {errors.confirmPassword}
          </label>
          <FormField
            placeholder='Confirm Password'
            type='password'
            id='confirmPassword'
            name={FormFields.ConfirmPassword}
            value={state.confirmPassword.value}
            error={errors?.confirmPassword}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className='flex flexDirectionColumn alignCenter'>
        <p className='passwordRequirements'>
          Password must be a minimum of 8 characters, contain a letter AND a number OR special
          character.
        </p>
        <p className='terms'>
          View our{' '}
          <button
            onClick={() => {
              Router.push(ROUTES.Terms).then(() => window.scrollTo(0, 0))
            }}
            type='button'
            className='flatBtn'
          >
            Terms and Conditions
          </button>{' '}
          or{' '}
          <button
            onClick={() => {
              Router.push(ROUTES.Privacy).then(() => window.scrollTo(0, 0))
            }}
            type='button'
            className='flatBtn'
          >
            Privacy Policy.
          </button>
        </p>
        <label
          className={classnames(
            'label',
            { error: errors.general || mappedServerErrors.general },
            { visible: errors.general || mappedServerErrors.general },
          )}
        >
          {errors.general || mappedServerErrors.general}
        </label>

        <Button type='submit' color={color} className='flatbtn signup' disabled={disabled}>
          Sign Up
        </Button>
      </div>

      <style jsx>{`
        .wrapper {
          position: relative;
          width: 43.6rem;
          padding: 2.6rem 2rem 4.1rem;
          background: #fff;
        }

        .title {
          padding: 0 0 0.8rem;
          line-height: 3.6rem;
          font-size: 3.2rem;
        }

        .label {
          display: none;
          margin: 0 0 1rem;
          color: var(--secondary-text-color);
          font-size: 1.4rem;

          &.visible {
            display: inline-block;
          }

          &.error {
            color: var(--alert-color);
          }
        }

        .loginInstead {
          padding: 0 0 2.3rem;
          line-height: 2.1rem;
          font-size: 1.6rem;

          button {
            color: var(--primary-color);
          }
        }

        .formFieldItem + .formFieldItem {
          padding: 1.7rem 0 0;
        }

        .formFieldItem {
          :global(#firstName, #lastName) {
            text-transform: capitalize;
          }
        }

        .passwordRequirements {
          max-width: 32rem;
          padding: 1.4rem 0 2.7rem;
          text-align: center;
          line-height: 1.9rem;
          font-size: 1.4rem;
          font-style: italic;
        }

        .terms {
          padding: 0 0 1.4rem;
          line-height: 1.9rem;
          text-align: center;
          font-size: 1.4rem;

          button {
            color: var(--primary-color);
          }
        }

        @media (max-width: 1024px) {
          .wrapper {
            width: 100%;
            padding: 1.6rem 2rem 4.1rem;
          }

          .title {
            padding: 0 0 0.5rem;
            line-height: 2.4rem;
            font-size: 1.8rem;
          }

          .loginInstead {
            padding: 0 0 1.6rem;
            line-height: 1.9rem;
            font-size: 1.4rem;
          }

          :global(.signup) {
            position: fixed;
            width: calc(100% - 2.8rem);
            bottom: 2.1rem;
          }
        }
      `}</style>
    </form>
  )
}

function validate(state) {
  const errors = {}
  let isValid = true

  for (const field of Object.keys(state)) {
    if (isFieldEmpty(state[field])) {
      isValid = false
      errors[field] = 'Required'
    }

    if (re[field]) {
      if (!isFieldValid(field, state)) {
        isValid = false
        errors[field] = validationErrors[field]
      }
    }

    if (
      field === FormFields.ConfirmPassword &&
      state.password.value !== state.confirmPassword.value
    ) {
      isValid = false
      errors[field] = validationErrors[field]
    }
  }

  return { isValid, errors }
}

function mapServerErrors(errors) {
  if (!errors) return {}

  const mappedErrors = {}

  errors.forEach((error) => {
    switch (parseInt(error?.code)) {
      case APIErrors.EmailAlreadyTaken:
        mappedErrors[FormFields.Email] = error.message
        break
      case APIErrors.PhoneNumberNotValid:
      case APIErrors.InvalidPhoneNumberFormat:
        mappedErrors[FormFields.PhoneNumber] = error.message
        break
      case APIErrors.ZipCodeDoesNotExists:
      case APIErrors.InvalidGoogleApiResponseStatusCode:
      case APIErrors.InvalidZipCode:
      case APIErrors.TimezoneNotFound:
      case APIErrors.TimezoneNotSupported:
        mappedErrors[FormFields.ZipCode] = validationErrors.zipCode
        break
      default:
        mappedErrors['general'] = error.message
        break
    }
  })

  return mappedErrors
}
